import React, { useState, useEffect, useMemo } from 'react';
import {
  X, Briefcase, Eye, Plus, Check, ChevronDown, ExternalLink,
} from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import SymbolAutocomplete from './SymbolAutocomplete';
import {
  getCachedPortfolioMap, setCachedPortfolioMap, updateCachedPortfolioMap, clearCachedPortfolioData,
  getCachedWatchlistMap, setCachedWatchlistMap, updateCachedWatchlistMap, clearCachedWatchlistData,
} from '../utils/portfolioWatchlistCache';
import { getCachedTradeDates } from '../utils/tradeDatesCache';
import { getCachedSymbolNamesSync } from '../utils/symbolNamesCache';
import { authFetch } from '../utils/authFetch';

const MAX_PORTFOLIOS = 2;
const MAX_WATCHLISTS = 3;
const MAX_SYMBOLS_PER_LIST = 10;

function resolveSymbolIdFromMap(raw, symbolMap) {
  if (!raw || !symbolMap) return null;
  const t = raw.trim();
  if (symbolMap[t]) return t;
  return Object.keys(symbolMap).find((k) => k.toUpperCase() === t.toUpperCase()) || null;
}

function parseStockTradeDate(tradeDateStr, allTradeDates) {
  if (!tradeDateStr || !allTradeDates?.length) return null;
  const ymd = String(tradeDateStr).split('T')[0].split(' ')[0];
  const d = new Date(`${ymd}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return allTradeDates.some((td) => td.getTime() === d.getTime()) ? d : null;
}

function newEmptyRow() {
  return {
    rowId: `row-${Date.now()}-${Math.random()}`,
    symbolId: null,
    symbol: '',
    price: '',
    qty: '1',
    actionType: 'long',
    date: null,
    fromScreener: false,
  };
}

function buildPortfolioRows(stocks, allTradeDates) {
  return stocks.map((stock) => ({
    rowId: stock.symbol_id || `row-${stock.symbol}-${Math.random()}`,
    symbolId: stock.symbol_id,
    symbol: stock.symbol || stock.symbol_id || '',
    price: stock.close != null ? String(Number(stock.close).toFixed(2)) : '',
    qty: '1',
    actionType: 'long',
    date: parseStockTradeDate(stock.trade_date, allTradeDates),
    fromScreener: true,
  }));
}

function buildWatchlistSymbols(stockList) {
  return stockList
    .filter((s) => s?.symbol_id)
    .map((s) => ({ symbol_id: s.symbol_id, symbol: s.symbol || s.symbol_id }));
}

export default function ScreenerBulkAddDialog({ stocks, onClose, onSuccess }) {
  const [step, setStep] = useState('choose');
  const [listType, setListType] = useState(null);
  const [existingLists, setExistingLists] = useState([]);
  const [existingListMap, setExistingListMap] = useState({});
  const [selectedList, setSelectedList] = useState(null);
  const [newListName, setNewListName] = useState('');
  const [rows, setRows] = useState([]);
  const [watchlistSymbols, setWatchlistSymbols] = useState([]);
  const [allTradeDates, setAllTradeDates] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [addedListName, setAddedListName] = useState('');
  const [addedSymbolCount, setAddedSymbolCount] = useState(0);
  const [pendingBulkConfirm, setPendingBulkConfirm] = useState(null);

  const [user] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('user'));
    } catch {
      return null;
    }
  });

  const selectionCount = stocks.length;

  useEffect(() => {
    const loadTradeDates = async () => {
      try {
        const dates = await getCachedTradeDates();
        const parsed = dates.map((d) => new Date(`${d}T00:00:00`));
        setAllTradeDates(parsed);
        setRows(buildPortfolioRows(stocks, parsed));
      } catch {
        setRows(buildPortfolioRows(stocks, []));
      }
    };
    loadTradeDates();
  }, [stocks]);

  const resolvedRows = useMemo(() => {
    const symbolMap = getCachedSymbolNamesSync() || {};
    return rows.map((r) => ({
      ...r,
      resolvedId: r.symbolId || resolveSymbolIdFromMap(r.symbol, symbolMap),
    }));
  }, [rows]);

  const activeRowCount = resolvedRows.filter((r) => r.resolvedId && r.qty !== '' && parseInt(r.qty, 10) !== 0).length;
  const watchlistCount = watchlistSymbols.length;

  const stockLookup = useMemo(() => {
    const byDisplay = new Map();
    const byId = new Map();
    stocks.forEach((s) => {
      if (!s?.symbol_id) return;
      byId.set(s.symbol_id, s);
      byDisplay.set((s.symbol || s.symbol_id).toUpperCase(), s);
      byDisplay.set(s.symbol_id.toUpperCase(), s);
    });
    return { byDisplay, byId };
  }, [stocks]);

  const countNewWatchlistSymbols = (listName) => {
    if (!listName) return 0;
    const current = existingListMap[listName];
    const arr = Array.isArray(current) ? current : [];
    return watchlistSymbols.filter((s) => !arr.includes(s.symbol_id)).length;
  };

  const canFitWatchlistInList = (listName) => (
    getListSymbolCount(listName) + countNewWatchlistSymbols(listName) <= MAX_SYMBOLS_PER_LIST
  );

  const countUniqueNewKeysForList = (listName) => {
    if (!listName) return 0;
    const positions = existingListMap[listName] || {};
    const uniqueNew = new Set();
    for (const r of resolvedRows) {
      if (!r.resolvedId || r.qty === '' || parseInt(r.qty, 10) === 0) continue;
      const key = `${r.resolvedId}|${r.actionType}`;
      if (!positions[key]) uniqueNew.add(key);
    }
    return uniqueNew.size;
  };

  const canFitRowsInList = (listName) => (
    getListSymbolCount(listName) + countUniqueNewKeysForList(listName) <= MAX_SYMBOLS_PER_LIST
  );

  const batchNewKeyCount = useMemo(() => {
    const uniqueNew = new Set(
      resolvedRows
        .filter((r) => r.resolvedId && r.qty !== '' && parseInt(r.qty, 10) !== 0)
        .map((r) => `${r.resolvedId}|${r.actionType}`),
    );
    return uniqueNew.size;
  }, [resolvedRows]);

  const allPageSelected = listType === 'watchlist'
    ? watchlistCount >= MAX_SYMBOLS_PER_LIST
    : batchNewKeyCount >= MAX_SYMBOLS_PER_LIST;

  const fetchLists = async (type) => {
    if (!user) return;
    try {
      if (type === 'portfolio') {
        const cached = getCachedPortfolioMap(user.email);
        if (cached) {
          setExistingListMap(cached);
          setExistingLists(Object.keys(cached));
          return;
        }
        const res = await authFetch(`/api/portfolio/${user.email}`);
        const data = await res.json();
        const map = data.portfolioMap || {};
        setCachedPortfolioMap(user.email, map);
        setExistingListMap(map);
        setExistingLists(Object.keys(map));
      } else {
        const cached = getCachedWatchlistMap(user.email);
        if (cached) {
          setExistingListMap(cached);
          setExistingLists(Object.keys(cached));
          return;
        }
        const res = await authFetch(`/api/watchlist/${user.email}`);
        const data = await res.json();
        const map = data.watchlistMap || {};
        setCachedWatchlistMap(user.email, map);
        setExistingListMap(map);
        setExistingLists(Object.keys(map));
      }
    } catch {
      setExistingListMap({});
      setExistingLists([]);
    }
  };

  const getListSymbolCount = (name) => {
    const listData = existingListMap[name];
    if (!listData) return 0;
    if (Array.isArray(listData)) return listData.length;
    if (typeof listData === 'object') return Object.keys(listData).length;
    return 0;
  };

  const isListSelectable = (name) => {
    if (listType === 'watchlist') {
      if (watchlistCount === 0) return false;
      if (!canFitWatchlistInList(name)) return false;
      if (allPageSelected && getListSymbolCount(name) > 0) return false;
      return true;
    }
    if (!canFitRowsInList(name)) return false;
    if (allPageSelected && getListSymbolCount(name) > 0) return false;
    return true;
  };

  const selectableLists = existingLists.filter(isListSelectable);
  const allExistingListsAreFull = existingLists.length > 0 && selectableLists.length === 0;
  const canCreateNewList = listType === 'portfolio'
    ? existingLists.length < MAX_PORTFOLIOS
    : existingLists.length < MAX_WATCHLISTS;

  const targetPositions = selectedList ? (existingListMap[selectedList] || {}) : {};

  const generateDefaultName = (type) => {
    const prefix = type === 'portfolio' ? 'portfolio_' : 'watchlist_';
    const existing = existingLists.filter((n) => n.startsWith(prefix));
    const nextNum = existing.length > 0
      ? Math.max(...existing.map((n) => {
        const m = n.match(new RegExp(`${prefix}(\\d+)`));
        return m ? parseInt(m[1], 10) : 0;
      })) + 1
      : 1;
    return `${prefix}${nextNum}`;
  };

  const handleChooseType = (type) => {
    setListType(type);
    setErrorMsg('');
    setSelectedList(null);
    setExistingListMap({});
    if (type === 'watchlist') {
      setWatchlistSymbols(buildWatchlistSymbols(stocks));
    }
    fetchLists(type);
    setStep('selectList');
  };

  const updateRow = (rowId, field, value) => {
    setRows((prev) => prev.map((r) => {
      if (r.rowId !== rowId) return r;
      const updated = { ...r, [field]: value };
      if (field === 'symbol') {
        const symbolMap = getCachedSymbolNamesSync() || {};
        updated.symbolId = resolveSymbolIdFromMap(value, symbolMap);
      }
      return updated;
    }));
  };

  const removeRow = (rowId) => {
    setRows((prev) => prev.filter((r) => r.rowId !== rowId));
  };

  const addRow = () => {
    if (rows.length >= MAX_SYMBOLS_PER_LIST) return;
    setRows((prev) => [...prev, newEmptyRow()]);
  };

  const canSubmitPortfolio = useMemo(() => {
    const valid = resolvedRows.filter((r) => r.resolvedId && r.qty !== '' && parseInt(r.qty, 10) !== 0);
    if (valid.length === 0) return false;
    return valid.every((row) => {
      const qty = parseInt(row.qty, 10);
      const compositeKey = `${row.resolvedId}|${row.actionType}`;
      const existingEntry = targetPositions[compositeKey];
      if (existingEntry) return true;
      return row.price !== '' && row.date && !Number.isNaN(qty) && qty >= 1;
    });
  }, [resolvedRows, targetPositions]);

  const canSubmitWatchlist = watchlistCount > 0;

  const watchlistTextValue = useMemo(
    () => watchlistSymbols.map((s) => (s.symbol || s.symbol_id).toUpperCase()).join(', '),
    [watchlistSymbols],
  );

  const syncWatchlistFromText = (text) => {
    const parts = text.split(',').map((p) => p.trim()).filter(Boolean);
    const kept = [];
    const seen = new Set();
    for (const part of parts) {
      const key = part.toUpperCase();
      const stock = stockLookup.byDisplay.get(key) || stockLookup.byId.get(part);
      if (stock && !seen.has(stock.symbol_id)) {
        seen.add(stock.symbol_id);
        kept.push({ symbol_id: stock.symbol_id, symbol: stock.symbol || stock.symbol_id });
      }
    }
    setWatchlistSymbols(kept);
  };

  const removeWatchlistSymbol = (symbolId) => {
    setWatchlistSymbols((prev) => prev.filter((s) => s.symbol_id !== symbolId));
  };

  const clearAllWatchlistSymbols = () => {
    setWatchlistSymbols([]);
  };

  const classifyPortfolioRows = (listName) => {
    const positions = existingListMap[listName] || {};
    const toAdd = [];
    const toUpdate = [];
    const toQtyUpdate = [];
    const willGoNegative = [];

    for (const r of resolvedRows) {
      if (!r.resolvedId || r.qty === '' || parseInt(r.qty, 10) === 0) continue;
      const compositeKey = `${r.resolvedId}|${r.actionType}`;
      const formattedDate = r.date ? r.date.toISOString().split('T')[0] : null;
      const qty = parseInt(r.qty, 10);
      const existingEntry = positions[compositeKey];

      if (existingEntry) {
        const existingQty = parseInt(existingEntry[3], 10);
        const finalQty = existingQty + qty;
        if (finalQty < 1) {
          willGoNegative.push({ ...r, compositeKey, existingQty, finalQty, formattedDate });
        } else if (r.price && formattedDate) {
          toUpdate.push({ ...r, compositeKey, formattedDate });
        } else {
          toQtyUpdate.push({ ...r, compositeKey, formattedDate });
        }
      } else {
        if (qty < 1) continue;
        if (!r.price || !formattedDate) {
          throw new Error(`Price and trade date required for new position ${r.symbol || r.resolvedId}`);
        }
        toAdd.push({ ...r, compositeKey, formattedDate });
      }
    }

    return { toAdd, toUpdate, toQtyUpdate, willGoNegative };
  };

  const processPortfolioChanges = async (listName, toAdd, toUpdate, toQtyUpdate, removeCompositeKeys) => {
    const currentDate = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
    let portfolioMap = existingListMap;

    if (removeCompositeKeys.length > 0) {
      const resp = await authFetch(`/api/portfolio/${user.email}/remove-multiple`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: listName, symbols: removeCompositeKeys }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.message);
      portfolioMap = data.portfolioMap;
    }

    if (toAdd.length > 0) {
      const payload = { name: listName };
      toAdd.forEach((r) => {
        payload[r.resolvedId] = [parseFloat(r.price), r.formattedDate, currentDate, parseInt(r.qty, 10), r.actionType];
      });
      const resp = await authFetch(`/api/portfolio/${user.email}/add-multiple`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.message);
      portfolioMap = data.portfolioMap;
    }

    for (const r of toUpdate) {
      const resp = await authFetch(`/api/portfolio/${user.email}/update-position`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: listName,
          symbol_id: r.resolvedId,
          additional_quantity: parseInt(r.qty, 10),
          new_price: parseFloat(r.price),
          buy_date: r.formattedDate,
          action_type: r.actionType,
        }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.message);
      portfolioMap = data.portfolioMap;
    }

    for (const r of toQtyUpdate) {
      const resp = await authFetch(`/api/portfolio/${user.email}/update-quantity`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: listName,
          symbol_id: r.resolvedId,
          action_type: r.actionType,
          additional_quantity: parseInt(r.qty, 10),
        }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.message);
      portfolioMap = data.portfolioMap;
    }

    if (portfolioMap) updateCachedPortfolioMap(user.email, portfolioMap);
    clearCachedPortfolioData(user.email, listName);
    setExistingListMap(portfolioMap || existingListMap);
  };

  const submitPortfolio = async (listName) => {
    const { toAdd, toUpdate, toQtyUpdate, willGoNegative } = classifyPortfolioRows(listName);
    const totalOps = toAdd.length + toUpdate.length + toQtyUpdate.length;
    if (totalOps === 0 && willGoNegative.length === 0) {
      throw new Error('Add at least one valid position');
    }
    if (willGoNegative.length > 0) {
      setPendingBulkConfirm({
        listName,
        toRemove: willGoNegative,
        toProcess: { toAdd, toUpdate, toQtyUpdate },
      });
      return 'pending';
    }
    await processPortfolioChanges(listName, toAdd, toUpdate, toQtyUpdate, []);
    setAddedSymbolCount(totalOps);
    return 'done';
  };

  const handleConfirmBulkRemove = async () => {
    if (!pendingBulkConfirm || !user) return;
    const { listName, toRemove, toProcess } = pendingBulkConfirm;
    setIsSubmitting(true);
    setErrorMsg('');
    try {
      await processPortfolioChanges(
        listName,
        toProcess.toAdd,
        toProcess.toUpdate,
        toProcess.toQtyUpdate,
        toRemove.map((r) => r.compositeKey),
      );
      setPendingBulkConfirm(null);
      setAddedListName(listName);
      setAddedSymbolCount(
        toProcess.toAdd.length + toProcess.toUpdate.length + toProcess.toQtyUpdate.length + toRemove.length,
      );
      setStep('success');
      onSuccess?.();
    } catch (err) {
      setErrorMsg(err.message || 'Something went wrong');
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitWatchlist = async (listName) => {
    if (watchlistSymbols.length === 0) throw new Error('Add at least one symbol');
    const payload = { name: listName, symbols: watchlistSymbols.map((s) => s.symbol_id) };
    const res = await authFetch(`/api/watchlist/${user.email}/add-multiple`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to add to watchlist');
    if (data.watchlistMap) updateCachedWatchlistMap(user.email, data.watchlistMap);
    clearCachedWatchlistData(user.email, listName);
    setAddedSymbolCount(watchlistSymbols.length);
  };

  const addToList = async (listName) => {
    if (!user) { setErrorMsg('Please log in'); return; }
    if (!listName) { setErrorMsg('No list selected'); return; }
    setIsSubmitting(true);
    setErrorMsg('');
    try {
      if (listType === 'portfolio') {
        const result = await submitPortfolio(listName);
        if (result === 'done') {
          setAddedListName(listName);
          setStep('success');
          onSuccess?.();
        }
      } else {
        await submitWatchlist(listName);
        setAddedListName(listName);
        setStep('success');
        onSuccess?.();
      }
    } catch (err) {
      setErrorMsg(err.message || 'Something went wrong');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateAndAdd = async () => {
    if (!user) return;
    setIsSubmitting(true);
    setErrorMsg('');
    const name = newListName.trim() || generateDefaultName(listType);
    try {
      const createUrl = listType === 'portfolio'
        ? `/api/portfolio/${user.email}/create`
        : `/api/watchlist/${user.email}/create`;
      const createRes = await authFetch(createUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      const createData = await createRes.json();
      if (!createRes.ok) throw new Error(createData.message || 'Failed to create list');
      if (listType === 'portfolio') {
        const map = createData.portfolioMap || {};
        updateCachedPortfolioMap(user.email, map);
        setExistingListMap(map);
      } else {
        updateCachedWatchlistMap(user.email, createData.watchlistMap || {});
      }
      setSelectedList(name);
      await addToList(name);
    } catch (err) {
      setErrorMsg(err.message || 'Something went wrong');
      setIsSubmitting(false);
    }
  };

  const renderPortfolioRows = () => (
    <div className="add-dialog-portfolio-section">
      <p className="screener-bulk-portfolio-hint">
        New symbols are added; existing positions are updated (±qty). Price + date optional for existing qty-only changes.
      </p>
      <div className="screener-bulk-portfolio-rows">
        <div className="pw-position-rows">
          {rows.map((row, idx) => {
            const resolvedId = row.symbolId || resolveSymbolIdFromMap(row.symbol, getCachedSymbolNamesSync() || {});
            const compositeKey = resolvedId ? `${resolvedId}|${row.actionType}` : null;
            const existingEntry = compositeKey ? targetPositions[compositeKey] : null;
            const existingQty = existingEntry ? parseInt(existingEntry[3], 10) : null;
            const deltaQty = parseInt(row.qty, 10) || 0;
            const projectedQty = existingQty !== null ? existingQty + deltaQty : null;
            const isExisting = existingEntry != null;

            return (
              <div key={row.rowId} className="pw-position-row-wrap">
                <div className="pw-position-row-label">
                  {isExisting ? (
                    <span className="pw-pos-hint pw-pos-existing">
                      {row.symbol || resolvedId} · Existing · {existingQty} shares @ ${parseFloat(existingEntry[0]).toFixed(2)}
                      {projectedQty !== null && (
                        <span className={`pw-pos-projected ${projectedQty < 1 ? 'pw-pos-danger' : projectedQty < existingQty ? 'pw-pos-warn' : 'pw-pos-ok'}`}>
                          &nbsp;→ {projectedQty < 1 ? 'remove position' : `${projectedQty} shares`}
                        </span>
                      )}
                    </span>
                  ) : resolvedId ? (
                    <span className="pw-pos-hint pw-pos-new">{row.symbol || resolvedId} · New position</span>
                  ) : (
                    <span className="pw-pos-hint pw-pos-empty">Row {idx + 1}</span>
                  )}
                </div>
                <div className="pw-form-row pw-position-row screener-bulk-position-row">
                  {!row.fromScreener && (
                    <div className="portfolio-symbol-input-wrapper">
                      <SymbolAutocomplete
                        value={row.symbol}
                        onChange={(v) => updateRow(row.rowId, 'symbol', v)}
                        placeholder="Symbol..."
                        className="symbol-input"
                        showValidation
                        maxSuggestions={10}
                      />
                    </div>
                  )}
                  <input
                    type="number"
                    value={row.price}
                    onChange={(e) => updateRow(row.rowId, 'price', e.target.value)}
                    placeholder="Price"
                    step="0.01"
                    className="symbol-input pw-buy-price"
                    title="Validated against day's trading range"
                  />
                  <input
                    type="number"
                    value={row.qty}
                    onChange={(e) => updateRow(row.rowId, 'qty', e.target.value)}
                    placeholder={isExisting ? '±Qty' : 'Qty'}
                    step="1"
                    className="symbol-input pw-qty-input"
                    title={isExisting ? 'Positive to add shares, negative to reduce' : 'Number of shares to add (min 1)'}
                  />
                  <select
                    value={row.actionType}
                    onChange={(e) => updateRow(row.rowId, 'actionType', e.target.value)}
                    className="symbol-input pw-action-select"
                  >
                    <option value="long">Long</option>
                    <option value="short">Short</option>
                  </select>
                  <div className="date-picker-wrapper">
                    <DatePicker
                      selected={row.date}
                      onChange={(date) => updateRow(row.rowId, 'date', date)}
                      includeDates={allTradeDates}
                      filterDate={(date) => allTradeDates.some((d) => d.getTime() === date.getTime())}
                      dateFormat="yyyy-MM-dd"
                      placeholderText="Trade Date"
                      showMonthDropdown
                      showYearDropdown
                      className="date-picker"
                    />
                    <ChevronDown className="date-picker-icon" />
                  </div>
                  <button
                    type="button"
                    className="pw-btn pw-row-remove-btn screener-bulk-row-remove"
                    onClick={() => removeRow(row.rowId)}
                    title="Remove this row"
                    aria-label={`Remove ${row.symbol || `row ${idx + 1}`}`}
                  >
                    <X size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {rows.length === 0 && (
        <p className="add-dialog-error screener-bulk-empty-rows">
          No symbols in this batch. Use Add more to include symbols.
        </p>
      )}
      <div className="pw-positions-footer screener-bulk-positions-footer">
        <div className="pw-positions-footer-left">
          <button
            type="button"
            className="pw-btn pw-btn-add-row"
            onClick={addRow}
            disabled={rows.length >= MAX_SYMBOLS_PER_LIST}
            title={rows.length >= MAX_SYMBOLS_PER_LIST ? 'Maximum 10 rows per batch' : 'Add another symbol'}
          >
            <Plus size={14} /> Add more
          </button>
        </div>
      </div>
    </div>
  );

  const renderWatchlistEditor = () => (
    <div className="screener-bulk-watchlist-editor">
      <div className="screener-bulk-watchlist-header">
        <p className="add-dialog-section-label">
          Symbols to add ({watchlistCount})
        </p>
        {watchlistCount > 0 && (
          <button
            type="button"
            className="screener-bulk-watchlist-clear"
            onClick={clearAllWatchlistSymbols}
          >
            Clear all
          </button>
        )}
      </div>
      <textarea
        className="pw-modal-input screener-bulk-watchlist-text"
        value={watchlistTextValue}
        onChange={(e) => syncWatchlistFromText(e.target.value)}
        placeholder="Comma-separated symbols"
        rows={2}
        aria-label="Symbols to add to watchlist"
      />
      {watchlistCount > 0 ? (
        <div className="screener-bulk-watchlist-chips">
          {watchlistSymbols.map((s) => (
            <span key={s.symbol_id} className="screener-bulk-watchlist-chip">
              {(s.symbol || s.symbol_id).toUpperCase()}
              <button
                type="button"
                className="screener-bulk-watchlist-chip-remove"
                onClick={() => removeWatchlistSymbol(s.symbol_id)}
                aria-label={`Remove ${s.symbol || s.symbol_id}`}
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      ) : (
        <p className="add-dialog-error screener-bulk-watchlist-empty">
          No symbols in this batch. Restore from your screener selection or go back.
        </p>
      )}
    </div>
  );

  const renderListOptions = () => (
    <>
      {allPageSelected && listType === 'watchlist' && (
        <p className="add-dialog-capacity-notice" role="status">
          {watchlistCount} symbols in this batch (max per list). Only empty watchlists can fit —
          remove symbols above or create a new watchlist.
        </p>
      )}
      {allPageSelected && listType === 'portfolio' && (
        <p className="add-dialog-capacity-notice" role="status">
          This batch adds {batchNewKeyCount} new position{batchNewKeyCount !== 1 ? 's' : ''} (max per list).
          Only empty portfolios can fit — remove rows, or pick a portfolio with room for fewer new symbols.
        </p>
      )}
      {existingLists.length > 0 && (
        <div className="add-dialog-list-section">
          <p className="add-dialog-section-label">
            Select an existing {listType === 'portfolio' ? 'portfolio' : 'watchlist'}:
          </p>
          <div className="add-dialog-list-options">
            {existingLists.map((name) => {
              const count = getListSymbolCount(name);
              const selectable = isListSelectable(name);
              const newKeys = listType === 'portfolio'
                ? countUniqueNewKeysForList(name)
                : countNewWatchlistSymbols(name);
              return (
                <button
                  key={name}
                  type="button"
                  className={`add-dialog-list-option ${selectedList === name ? 'selected' : ''} ${!selectable ? 'disabled' : ''}`}
                  onClick={() => selectable && handleSelectExisting(name)}
                  disabled={!selectable}
                  title={
                    !selectable
                      ? `${count}/${MAX_SYMBOLS_PER_LIST}${listType === 'portfolio' ? ` · +${newKeys} new` : ''} — not enough room`
                      : undefined
                  }
                >
                  {listType === 'portfolio' ? <Briefcase size={15} /> : <Eye size={15} />}
                  <span className="add-dialog-list-option-name">{name}</span>
                  <span className="add-dialog-list-option-count">
                    {count}/{MAX_SYMBOLS_PER_LIST}
                    {newKeys > 0 ? ` (+${newKeys} new)` : ''}
                  </span>
                  {selectedList === name && <Check size={15} className="add-dialog-check" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </>
  );

  const handleSelectExisting = (name) => {
    setSelectedList(name);
    setErrorMsg('');
  };

  if (!user) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="pw-modal add-dialog screener-bulk-add-dialog" onClick={(e) => e.stopPropagation()}>
          <div className="pw-modal-content">
            <button type="button" className="add-dialog-close" onClick={onClose}><X size={18} /></button>
            <h3>Sign in Required</h3>
            <p className="pw-modal-description">Please log in to add stocks to your portfolio or watchlist.</p>
            <div className="pw-modal-actions">
              <button type="button" onClick={onClose} className="pw-btn pw-btn-primary">OK</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="pw-modal add-dialog screener-bulk-add-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="pw-modal-content">
          <button type="button" className="add-dialog-close" onClick={onClose}><X size={18} /></button>

          {step === 'choose' && (
            <>
              <h3>Add {selectionCount} symbol{selectionCount !== 1 ? 's' : ''}</h3>
              <p className="pw-modal-description">Where would you like to add the selected stocks?</p>
              <div className="add-dialog-type-buttons">
                <button type="button" className="add-dialog-type-btn" onClick={() => handleChooseType('portfolio')}>
                  <Briefcase size={22} />
                  <span>Portfolio</span>
                </button>
                <button type="button" className="add-dialog-type-btn" onClick={() => handleChooseType('watchlist')}>
                  <Eye size={22} />
                  <span>Watchlist</span>
                </button>
              </div>
            </>
          )}

          {step === 'selectList' && (
            <>
              <h3>Add to {listType === 'portfolio' ? 'Portfolio' : 'Watchlist'}</h3>
              {listType === 'watchlist' && renderWatchlistEditor()}
              {listType === 'portfolio' && renderPortfolioRows()}
              {renderListOptions()}

              {allExistingListsAreFull && (
                <div className="add-dialog-list-section">
                  <p className="add-dialog-error">
                    {allPageSelected
                      ? 'No empty portfolios available. Create a new portfolio or remove rows from this batch.'
                      : `Not enough room in existing ${listType === 'portfolio' ? 'portfolios' : 'watchlists'}.`}
                  </p>
                </div>
              )}

              {canCreateNewList && (
                <>
                  <div className="add-dialog-divider"><span>or</span></div>
                  <button type="button" className="add-dialog-create-new-btn" onClick={() => setStep('createNew')}>
                    <Plus size={16} /> Create new {listType === 'portfolio' ? 'portfolio' : 'watchlist'}
                  </button>
                </>
              )}

              {errorMsg && <p className="add-dialog-error">{errorMsg}</p>}

              <div className="pw-modal-actions">
                <button type="button" onClick={() => { setStep('choose'); setErrorMsg(''); }} className="pw-btn pw-btn-ghost">Back</button>
                <button
                  type="button"
                  onClick={() => addToList(selectedList)}
                  className="pw-btn pw-btn-modal"
                  disabled={
                    !selectedList
                    || isSubmitting
                    || (listType === 'portfolio' && !canSubmitPortfolio)
                    || (listType === 'watchlist' && !canSubmitWatchlist)
                    || !isListSelectable(selectedList)
                  }
                >
                  {isSubmitting ? 'Adding…' : 'Add'}
                </button>
              </div>
            </>
          )}

          {step === 'createNew' && (
            <>
              <h3>New {listType === 'portfolio' ? 'Portfolio' : 'Watchlist'}</h3>
              <p className="pw-modal-description">Give it a name, or leave empty for a default.</p>
              <input
                type="text"
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
                placeholder={listType === 'portfolio' ? 'e.g. Growth Stocks' : 'e.g. Tech Stocks'}
                className="pw-modal-input"
              />
              {listType === 'watchlist' && renderWatchlistEditor()}
              {listType === 'portfolio' && renderPortfolioRows()}
              {errorMsg && <p className="add-dialog-error">{errorMsg}</p>}
              <div className="pw-modal-actions">
                <button type="button" onClick={() => { setStep('selectList'); setErrorMsg(''); }} className="pw-btn pw-btn-ghost">Back</button>
                <button
                  type="button"
                  onClick={handleCreateAndAdd}
                  className="pw-btn pw-btn-primary"
                  disabled={isSubmitting || (listType === 'portfolio' && !canSubmitPortfolio) || (listType === 'watchlist' && !canSubmitWatchlist)}
                >
                  {isSubmitting ? 'Creating…' : 'Create & Add'}
                </button>
              </div>
            </>
          )}

          {step === 'success' && (
            <div className="add-dialog-success">
              <div className="add-dialog-success-icon"><Check size={28} /></div>
              <h3>{addedSymbolCount} symbol{addedSymbolCount !== 1 ? 's' : ''} added!</h3>
              <p className="pw-modal-description">
                Successfully added to <strong>{addedListName}</strong>
              </p>
              <div className="add-dialog-success-actions">
                <a
                  href={listType === 'portfolio' ? '/portfolio' : '/watchlist'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pw-btn pw-btn-primary"
                  onClick={onClose}
                >
                  Go to {listType === 'portfolio' ? 'Portfolio' : 'Watchlist'} <ExternalLink size={14} />
                </a>
              </div>
            </div>
          )}
        </div>
      </div>

      {pendingBulkConfirm && (
        <div className="modal-overlay add-dialog-confirm-overlay" onClick={() => !isSubmitting && setPendingBulkConfirm(null)}>
          <div className="pw-modal pw-modal-warn" onClick={(e) => e.stopPropagation()}>
            <div className="pw-modal-content">
              <h3 className="pw-modal-warn-title">Position Quantity Would Drop Below 1</h3>
              <p className="pw-modal-description">
                The following would reach 0 or negative quantity. Remove them and apply the rest, or go back to adjust.
              </p>
              <ul className="pw-modal-qty-list">
                {pendingBulkConfirm.toRemove.map((item) => (
                  <li key={item.compositeKey} className="pw-modal-qty-item">
                    <span className="pw-modal-qty-symbol">{item.symbol || item.resolvedId}</span>
                    <span className="pw-modal-qty-math">
                      {item.existingQty} + ({parseInt(item.qty, 10)}) ={' '}
                      <strong className="pw-pos-danger">{item.finalQty}</strong>
                    </span>
                  </li>
                ))}
              </ul>
              <div className="pw-modal-actions">
                <button
                  type="button"
                  onClick={() => setPendingBulkConfirm(null)}
                  className="pw-btn pw-btn-secondary"
                  disabled={isSubmitting}
                >
                  Go Back
                </button>
                <button
                  type="button"
                  onClick={handleConfirmBulkRemove}
                  className="pw-btn pw-btn-danger"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Updating…' : 'Remove & Continue'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
